import { Badge, Button, EmptyState, Table, type TableSortDirection } from '@ntconsult/aluma-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PercentageCheckColumnsState } from '../hooks/usePercentageCheckColumns';
import type { PercentageCheckFilterState } from '../hooks/usePercentageCheckFilters';
import type {
  PercentageCheckFilterOptions,
  PercentageCheckRangeKey,
  PercentageCheckRow,
  PercentageCheckRowBucket,
  PercentageCheckSortField,
} from '../types/PercentageCheck';
import { formatDisplayDate } from '../utils/data';
import {
  COLUMN_MIN_WIDTH,
  PINNED_BLOCK,
  STICKY_END_CELL,
  STICKY_END_HEADER,
  WIDE_TABLE_CONTAINER,
} from './wideTable';

type TableProps = {
  items: PercentageCheckRow[];
  filterOptions: PercentageCheckFilterOptions;
  columnsState: PercentageCheckColumnsState;
  filterState: PercentageCheckFilterState;
};

const PERCENTAGE_STEP = 5;

export function PercentageCheckTable({
  items,
  filterOptions,
  columnsState,
  filterState,
}: TableProps) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { isVisible, visibleColumns } = columnsState;
  const { filters, setIds, setRange, setSort } = filterState;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortProps = (field: PercentageCheckSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  const rangeFilter = (key: PercentageCheckRangeKey, label: string, step = 1) => (
    <Table.NumberRangeFilter
      label={label}
      step={step}
      value={{ min: filters[`${key}From`] ?? null, max: filters[`${key}To`] ?? null }}
      onValueChange={(range) => setRange(key, range)}
    />
  );

  const col = (key: string) =>
    t(`alocacao:consultas.conferenciaPercentual.colunas.${key}` as never);

  const columnCount = visibleColumns.length + 2;

  return (
    <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
      <Table.Caption className="sr-only">
        {t('alocacao:consultas.conferenciaPercentual.titulo')}
      </Table.Caption>
      <Table.Head>
        <Table.Row>
          <Table.Header className={COLUMN_MIN_WIDTH.medium} {...sortProps('professional')}>
            {col('profissional')}
          </Table.Header>
          {isVisible('unit') && (
            <Table.Header
              className="whitespace-nowrap"
              {...sortProps('unit')}
              filter={
                <Table.OptionsFilter
                  label={col('unidade')}
                  options={filterOptions.units.map((unit) => ({
                    value: String(unit.id),
                    label: unit.code,
                  }))}
                  value={(filters.unitIds ?? []).map(String)}
                  onValueChange={(values) => setIds('unitIds', values.map(Number))}
                />
              }
            >
              {col('unidade')}
            </Table.Header>
          )}
          {isVisible('contractType') && (
            <Table.Header
              className="whitespace-nowrap"
              {...sortProps('contractType')}
              filter={
                <Table.OptionsFilter
                  label={col('tipoContrato')}
                  options={filterOptions.contractTypes.map((type) => ({
                    value: String(type.id),
                    label: type.name,
                  }))}
                  value={(filters.contractTypeIds ?? []).map(String)}
                  onValueChange={(values) => setIds('contractTypeIds', values.map(Number))}
                />
              }
            >
              {col('tipoContrato')}
            </Table.Header>
          )}
          {isVisible('contractStatus') && (
            <Table.Header className="whitespace-nowrap">{col('situacaoContrato')}</Table.Header>
          )}
          {isVisible('maxPercentage') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('maxPercentage')}
              filter={rangeFilter('maxPercentage', col('maximo'), PERCENTAGE_STEP)}
            >
              {col('maximo')}
            </Table.Header>
          )}
          {isVisible('minPercentage') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('minPercentage')}
              filter={rangeFilter('minPercentage', col('minimo'), PERCENTAGE_STEP)}
            >
              {col('minimo')}
            </Table.Header>
          )}
          {isVisible('avgPercentage') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('avgPercentage')}
            >
              {col('media')}
            </Table.Header>
          )}
          {isVisible('daysBelow') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('daysBelow')}
              filter={rangeFilter('daysBelow', col('diasAbaixo'))}
            >
              {col('diasAbaixo')}
            </Table.Header>
          )}
          {isVisible('daysAt100') && (
            <Table.Header align="right" className="whitespace-nowrap">
              {col('diasEm100')}
            </Table.Header>
          )}
          {isVisible('daysAbove') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('daysAbove')}
              filter={rangeFilter('daysAbove', col('diasAcima'))}
            >
              {col('diasAcima')}
            </Table.Header>
          )}
          {isVisible('classification') && (
            <Table.Header className="whitespace-nowrap">{col('classificacao')}</Table.Header>
          )}
          <Table.Header className={STICKY_END_HEADER} aria-label={t('alocacao:tabela.acoes')} />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <div className={PINNED_BLOCK}>
                <EmptyState
                  title={t('alocacao:consultas.conferenciaPercentual.vazioTitulo')}
                  description={t('alocacao:consultas.conferenciaPercentual.vazioDescricao')}
                  action={
                    <Button variant="secondary" onClick={filterState.clearFilters}>
                      {t('alocacao:filtros.limpar')}
                    </Button>
                  }
                />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          items.map((row) => {
            const isOpen = expanded.has(row.professionalId);
            const canExpand = (row.allocations?.length ?? 0) > 0 || row.hiddenAllocationsCount > 0;
            return (
              <Fragment key={row.professionalId}>
                <Table.Row
                  interactive={canExpand}
                  aria-expanded={canExpand ? isOpen : undefined}
                  onClick={canExpand ? () => toggle(row.professionalId) : undefined}
                >
                  <Table.Cell className="font-medium">
                    <span className="tabular-nums text-foreground-muted">
                      {row.registrationNumber ?? '—'}
                    </span>{' '}
                    · {row.professionalName}
                  </Table.Cell>
                  {isVisible('unit') && <Table.Cell>{row.unitCode ?? '—'}</Table.Cell>}
                  {isVisible('contractType') && <Table.Cell>{row.contractType ?? '—'}</Table.Cell>}
                  {isVisible('contractStatus') && <Table.Cell>{row.contractStatus}</Table.Cell>}
                  {isVisible('maxPercentage') && (
                    <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                      {row.maxPercentage}%
                    </Table.Cell>
                  )}
                  {isVisible('minPercentage') && (
                    <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                      {row.minPercentage}%
                    </Table.Cell>
                  )}
                  {isVisible('avgPercentage') && (
                    <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
                      {row.avgPercentage.toFixed(1)}%
                    </Table.Cell>
                  )}
                  {isVisible('daysBelow') && (
                    <Table.Cell align="right" className="tabular-nums">
                      {row.daysBelow + row.daysZero}
                    </Table.Cell>
                  )}
                  {isVisible('daysAt100') && (
                    <Table.Cell align="right" className="tabular-nums">
                      {row.daysAt100}
                    </Table.Cell>
                  )}
                  {isVisible('daysAbove') && (
                    <Table.Cell align="right" className="tabular-nums">
                      {row.daysAbove}
                    </Table.Cell>
                  )}
                  {isVisible('classification') && (
                    <Table.Cell>
                      <BucketBadge bucket={row.bucket} />
                    </Table.Cell>
                  )}
                  <Table.Cell className={STICKY_END_CELL} align="right">
                    {canExpand && (
                      <span className="inline-flex p-1.5 text-foreground-muted" aria-hidden="true">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                    )}
                  </Table.Cell>
                </Table.Row>
                {isOpen && (
                  <Table.Row>
                    <Table.Cell colSpan={columnCount} className="bg-surface-hover">
                      <ExpandedDetail row={row} language={i18n.language} />
                    </Table.Cell>
                  </Table.Row>
                )}
              </Fragment>
            );
          })
        )}
      </Table.Body>
    </Table>
  );
}

function BucketBadge({ bucket }: { bucket: PercentageCheckRowBucket }) {
  const { t } = useTranslation('alocacao');
  const tone: 'warning' | 'success' | 'danger' | 'neutral' =
    bucket === 'above'
      ? 'danger'
      : bucket === 'at100'
        ? 'success'
        : bucket === 'below'
          ? 'warning'
          : 'neutral';
  return (
    <Badge variant="soft" tone={tone} size="sm">
      {t(`consultas.conferenciaPercentual.classificacao.${bucket}` as const)}
    </Badge>
  );
}

function ExpandedDetail({ row, language }: { row: PercentageCheckRow; language: string }) {
  const { t } = useTranslation('alocacao');
  const hidden = row.hiddenAllocationsCount;
  return (
    <div className="flex flex-col gap-3 p-3">
      <p className="text-sm font-semibold">
        {t('consultas.conferenciaPercentual.detalhes.tituloAdmin')}
      </p>
      {(row.allocations ?? []).length === 0 ? (
        <p className="text-sm text-foreground-muted">
          {t('consultas.conferenciaPercentual.detalhes.tituloAdmin')}: —
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(row.allocations ?? []).map((allocation) => (
            <li
              key={allocation.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded border border-border p-2 text-sm"
            >
              <span className="min-w-0 truncate">
                {allocation.projectAxisCode ? `${allocation.projectAxisCode} · ` : ''}
                {allocation.projectName}
              </span>
              <span className="flex items-center gap-3 text-foreground-muted">
                <span className="tabular-nums">{allocation.allocatedPercentage}%</span>
                <span className="tabular-nums">
                  {formatDisplayDate(allocation.startDate, language)}
                  {' → '}
                  {allocation.endDate ? formatDisplayDate(allocation.endDate, language) : '∞'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {hidden > 0 && (
        <p className="text-xs text-foreground-muted">
          {t(
            hidden === 1
              ? 'consultas.conferenciaPercentual.detalhes.gestorOculto'
              : 'consultas.conferenciaPercentual.detalhes.gestorOcultoPlural',
            { count: hidden },
          )}
        </p>
      )}
    </div>
  );
}
